import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, MailIcon, EditIcon, PrinterIcon, CheckCircleIcon, XCircleIcon, MoreHorizontalIcon, ArchiveIcon, TrashIcon, UserIcon, PhoneIcon, MapPinIcon, CreditCardIcon, TagIcon, FileTextIcon, AlertTriangleIcon, ClockIcon, PackageIcon, TruckIcon, CalendarIcon, CheckIcon } from 'lucide-react';
import { Order, OrderStatus } from './Orders';
export function OrderDetail() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // Mock data based on the ID or default
  const order: Order = {
    id: id || '220629-1e753',
    status: 'new',
    paymentStatus: 'paid',
    customer: {
      name: 'randy',
      email: 'randy@randy.com',
      address: 'randy',
      mobileNumber: '6596605522',
      additionalPhone: '1234',
      address2: '1234'
    },
    items: [{
      name: 'Kittens',
      quantity: 2,
      price: 111.0
    }],
    total: 222.0,
    date: '29/06/2022',
    time: '14:28',
    transaction: '62bc1b4be3c2250009f1e753',
    transactionStatus: 'confirmed',
    notes: 'Please deliver between 9 AM and 5 PM.',
    shopId: '1',
    shopName: 'Beach Shop'
  };
  const getStatusBadge = (status: OrderStatus) => {
    const styles = {
      new: 'bg-blue-50 text-blue-700 border-blue-200',
      'in-progress': 'bg-amber-50 text-amber-700 border-amber-200',
      open: 'bg-purple-50 text-purple-700 border-purple-200',
      completed: 'bg-green-50 text-green-700 border-green-200',
      cancelled: 'bg-red-50 text-red-700 border-red-200'
    };
    const labels = {
      new: 'New',
      'in-progress': 'In Progress',
      open: 'Open',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${styles[status]}`} data-id="element-11197">
        {labels[status]}
      </span>;
  };
  const getPaymentMethod = (transaction?: string) => {
    if (!transaction) return 'Unknown payment method';
    if (transaction.startsWith('62')) return 'Credit Card';
    return 'Bank Transfer';
  };
  const getShippingStatus = (status: OrderStatus) => {
    switch (status) {
      case 'new':
        return 'Awaiting processing';
      case 'in-progress':
        return 'Processing';
      case 'open':
        return 'Ready for pickup';
      case 'completed':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };
  const formatDate = (dateString: string) => {
    const parts = dateString.split('/');
    if (parts.length !== 3) return dateString;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const year = parseInt(parts[2]);
    return `${day} ${months[month]} ${year}`;
  };
  return <div className="max-w-[1400px] mx-auto space-y-6" data-id="element-11198">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-11199">
        <div className="flex items-center gap-4" data-id="element-11200">
          <button onClick={() => navigate('/orders')} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-11201">
            <ArrowLeftIcon size={18} data-id="element-11202" />
          </button>
          <div data-id="element-11203">
            <div className="flex items-center gap-3" data-id="element-11204">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-11205">
                {t('pages.OrderDetail.order')}
                {order.id}
              </h1>
              {getStatusBadge(order.status)}
            </div>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-11206">
              {order.customer.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-11207">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-11208">
            <MailIcon size={16} data-id="element-11209" />
            {t('pages.OrderDetail.send')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-11210">
            <DownloadIcon size={16} data-id="element-11211" />
            {t('pages.OrderDetail.download')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11212">
            <CheckCircleIcon size={16} data-id="element-11213" />
            {t('pages.OrderDetail.mark_fulfilled')}
          </button>
          <div className="relative" data-id="element-11214">
            <button onClick={() => setShowMoreMenu(!showMoreMenu)} aria-label="More actions" aria-expanded={showMoreMenu} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-11215">
              <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-11216" />
            </button>

            {showMoreMenu && <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMoreMenu(false)} data-id="element-11217" />

                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-11218">
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-11219">
                    <PrinterIcon size={16} className="mr-3 text-gray-400" data-id="element-11220" />
                    {t('pages.OrderDetail.print_order')}
                  </button>
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-11221">
                    <EditIcon size={16} className="mr-3 text-gray-400" data-id="element-11222" />
                    {t('pages.OrderDetail.edit_order')}
                  </button>
                  <div className="border-t border-gray-100 my-1" data-id="element-11223" />
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-11224">
                    <TrashIcon size={16} className="mr-3" data-id="element-11225" />
                    {t('pages.OrderDetail.cancel_order')}
                  </button>
                </div>
              </>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-11226">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6" data-id="element-11227">
          {/* Order Items */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11228">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-11229">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11230">
                <PackageIcon size={18} className="text-gray-500" data-id="element-11231" />
                {t('pages.OrderDetail.order_items')}
              </h2>
            </div>
            <div className="p-6" data-id="element-11232">
              <div className="space-y-4" data-id="element-11233">
                {order.items.map((item, index) => <div key={index} className="flex justify-between border-b border-gray-100 pb-4 last:border-0 last:pb-0" data-id="element-11234">
                    <div data-id="element-11235">
                      <div className="font-medium text-gray-900" data-id="element-11236">
                        {item.name}
                      </div>
                      <div className="text-sm text-gray-500 mt-1" data-id="element-11237">
                        {t('pages.OrderDetail.quantity')} {item.quantity} ×{' '}
                        {formatCurrencyAmount(item.price, {
                      fractionDigits: 2
                    })}
                      </div>
                    </div>
                    <div className="font-medium text-gray-900" data-id="element-11238">
                      {formatCurrencyAmount(item.price * item.quantity, {
                    fractionDigits: 2
                  })}
                    </div>
                  </div>)}
              </div>

              <div className="mt-6 border-t border-gray-100 pt-4 space-y-3" data-id="element-11239">
                <div className="flex justify-between text-sm" data-id="element-11240">
                  <span className="text-gray-500" data-id="element-11241">
                    {t('pages.OrderDetail.subtotal')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-11242">
                    {formatCurrencyAmount(order.total, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-11243">
                  <span className="text-gray-500" data-id="element-11244">
                    {t('pages.OrderDetail.shipping')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-11245">
                    {formatCurrencyAmount(0, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-11246">
                  <span className="text-gray-500" data-id="element-11247">
                    {t('pages.OrderDetail.tax')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-11248">
                    {formatCurrencyAmount(0, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-base font-semibold pt-3 border-t border-gray-100" data-id="element-11249">
                  <span className="text-gray-900" data-id="element-11250">
                    {t('pages.OrderDetail.total')}
                  </span>
                  <span className="text-[#9F140F]" data-id="element-11251">
                    {formatCurrencyAmount(order.total, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11252">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11253">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11254">
                <ClockIcon size={18} className="text-gray-500" data-id="element-11255" />
                {t('pages.OrderDetail.order_timeline')}
              </h2>
            </div>
            <div className="p-6" data-id="element-11256">
              <div className="space-y-6" data-id="element-11257">
                <div className="flex items-start" data-id="element-11258">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5" data-id="element-11259">
                    <CheckIcon size={14} className="text-green-600" data-id="element-11260" />
                  </div>
                  <div className="ml-4" data-id="element-11261">
                    <p className="text-sm font-medium text-gray-900" data-id="element-11262">
                      {t('pages.OrderDetail.order_placed')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-11263">
                      {formatDate(order.date)} {t('pages.OrderDetail.at')}{' '}
                      {order.time}
                    </p>
                  </div>
                </div>

                {order.status === 'in-progress' || order.status === 'completed' ? <div className="flex items-start" data-id="element-11264">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mt-0.5" data-id="element-11265">
                      <CheckIcon size={14} className="text-amber-600" data-id="element-11266" />
                    </div>
                    <div className="ml-4" data-id="element-11267">
                      <p className="text-sm font-medium text-gray-900" data-id="element-11268">
                        {t('pages.OrderDetail.processing_started')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-11269">
                        {t('pages.OrderDetail.order_is_being_processed')}
                      </p>
                    </div>
                  </div> : <div className="flex items-start" data-id="element-11270">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5" data-id="element-11271">
                      <span className="text-xs font-medium text-gray-500" data-id="element-11272">
                        2
                      </span>
                    </div>
                    <div className="ml-4" data-id="element-11273">
                      <p className="text-sm font-medium text-gray-500" data-id="element-11274">
                        {t('pages.OrderDetail.processing')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-11275">
                        {t('pages.OrderDetail.waiting_to_be_processed')}
                      </p>
                    </div>
                  </div>}

                {order.status === 'completed' ? <div className="flex items-start" data-id="element-11276">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5" data-id="element-11277">
                      <CheckIcon size={14} className="text-green-600" data-id="element-11278" />
                    </div>
                    <div className="ml-4" data-id="element-11279">
                      <p className="text-sm font-medium text-gray-900" data-id="element-11280">
                        {t('components.getOrderStatusBadge.completed')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-11281">
                        {t('pages.OrderDetail.order_has_been_fulfilled')}
                      </p>
                    </div>
                  </div> : <div className="flex items-start" data-id="element-11282">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5" data-id="element-11283">
                      <span className="text-xs font-medium text-gray-500" data-id="element-11284">
                        3
                      </span>
                    </div>
                    <div className="ml-4" data-id="element-11285">
                      <p className="text-sm font-medium text-gray-500" data-id="element-11286">
                        {t('components.getOrderStatusBadge.completed')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-11287">
                        {t('pages.OrderDetail.waiting_to_be_completed')}
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-11288">
          {/* Customer Information */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11289">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11290">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11291">
                <UserIcon size={18} className="text-gray-500" data-id="element-11292" />
                {t('pages.OrderDetail.customer_information')}
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-11293">
              <div className="flex items-center gap-3 mb-2" data-id="element-11294">
                <div className="w-10 h-10 bg-[#E2231A]/10 rounded-full flex items-center justify-center text-[#9F140F] font-semibold" data-id="element-11295">
                  {order.customer.name.charAt(0).toUpperCase()}
                </div>
                <div data-id="element-11296">
                  <div className="font-medium text-gray-900" data-id="element-11297">
                    {order.customer.name}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-11298">
                    {t('pages.OrderDetail.customer')}
                    {order.id.split('-')[0]}
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-2 border-t border-gray-100" data-id="element-11299">
                <div className="flex items-start gap-3" data-id="element-11300">
                  <MailIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11301" />
                  <div data-id="element-11302">
                    <div className="text-sm text-gray-900" data-id="element-11303">
                      {order.customer.email}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11304">
                      {t('pages.OrderDetail.email')}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-11305">
                  <PhoneIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11306" />
                  <div data-id="element-11307">
                    <div className="text-sm text-gray-900" data-id="element-11308">
                      {order.customer.mobileNumber}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11309">
                      {t('pages.OrderDetail.mobile')}
                    </div>
                  </div>
                </div>
                {order.customer.additionalPhone && <div className="flex items-start gap-3" data-id="element-11310">
                    <PhoneIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11311" />
                    <div data-id="element-11312">
                      <div className="text-sm text-gray-900" data-id="element-11313">
                        {order.customer.additionalPhone}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-11314">
                        {t('pages.OrderDetail.additional_phone')}
                      </div>
                    </div>
                  </div>}
                <div className="flex items-start gap-3" data-id="element-11315">
                  <MapPinIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11316" />
                  <div data-id="element-11317">
                    <div className="text-sm text-gray-900" data-id="element-11318">
                      {order.customer.address}
                    </div>
                    {order.customer.address2 && <div className="text-sm text-gray-900 mt-0.5" data-id="element-11319">
                        {order.customer.address2}
                      </div>}
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-11320">
                      {t('pages.OrderDetail.shipping_address')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11321">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11322">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11323">
                <CreditCardIcon size={18} className="text-gray-500" data-id="element-11324" />
                {t('pages.OrderDetail.payment_information')}
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-11325">
              <div className="flex items-start gap-3" data-id="element-11326">
                <CreditCardIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11327" />
                <div data-id="element-11328">
                  <div className="text-sm text-gray-900" data-id="element-11329">
                    {getPaymentMethod(order.transaction)}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-11330">
                    {t('pages.OrderDetail.payment_method')}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-11331">
                <TagIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11332" />
                <div data-id="element-11333">
                  <div className="text-sm text-gray-900 capitalize" data-id="element-11334">
                    {order.paymentStatus}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-11335">
                    {t('pages.OrderDetail.payment_status')}
                  </div>
                </div>
              </div>
              {order.transaction && <div className="flex items-start gap-3" data-id="element-11336">
                  <FileTextIcon size={16} className="text-gray-400 mt-0.5" data-id="element-11337" />
                  <div data-id="element-11338">
                    <div className="text-sm text-gray-900 break-all" data-id="element-11339">
                      {order.transaction}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11340">
                      {t('pages.OrderDetail.transaction_id')}
                    </div>
                  </div>
                </div>}
              {order.transactionStatus && <div className="flex items-start gap-3" data-id="element-11341">
                  {order.transactionStatus === 'confirmed' ? <CheckCircleIcon size={16} className="text-green-500 mt-0.5" data-id="element-11342" /> : order.transactionStatus === 'failed' ? <AlertTriangleIcon size={16} className="text-red-500 mt-0.5" data-id="element-11343" /> : <ClockIcon size={16} className="text-amber-500 mt-0.5" data-id="element-11344" />}
                  <div data-id="element-11345">
                    <div className="text-sm text-gray-900 capitalize" data-id="element-11346">
                      {order.transactionStatus}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11347">
                      {t('pages.OrderDetail.transaction_status')}
                    </div>
                  </div>
                </div>}
            </div>
          </div>

          {/* Shipping Status */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11348">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-11349">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-11350">
                <TruckIcon size={18} className="text-gray-500" data-id="element-11351" />
                {t('pages.OrderDetail.shipping_status')}
              </h2>
            </div>
            <div className="p-6" data-id="element-11352">
              <div className="text-sm font-medium text-gray-900 mb-4" data-id="element-11353">
                {getShippingStatus(order.status)}
              </div>
              {order.notes && <div className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-11354">
                  <div className="text-xs font-medium text-gray-700 mb-1" data-id="element-11355">
                    {t('pages.OrderDetail.customer_notes')}
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-11356">{order.notes}</p>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}